import React, { useState, useEffect, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaInstagram, FaLinkedin, FaFacebook, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import heroVideo from '../assets/videos/v4.mp4';

import { developers } from '../data/developers';

const propertyImages = [
  '/property1.jpg',
  '/property2.jpg',
  '/pexels-katetrysh-281502.jpg',
];

const faqs = [
  { q: 'Why invest in off-plan properties in Dubai?', a: 'Dubai\'s off-plan market offers lower entry prices, flexible payment plans, and high ROI potential (typically 20-30% upon completion), combined with zero property taxes and a booming rental market.' },
  { q: 'What makes Alpha Ahead Real Estate different?', a: 'We specialize in pre-launch access to premium developments, with a curated selection of projects from trusted developers that pass our strict ROI and quality criteria.' },
  { q: 'Are off-plan investments safe?', a: 'Yes. Dubai\'s RERA regulations protect investors, requiring developers to place funds in escrow accounts. We only work with top-tier, verified developers with proven delivery records.' },
  { q: 'What are the payment terms?', a: 'Most projects offer installment plans (typically 10-30% downpayment with the balance spread over construction). Some elite developments we feature have post-handover payment options.' },
  { q: 'Which areas offer the best ROI?', a: 'Current hotspots: Downtown Dubai (15-20% annual appreciation), Dubai Creek Harbour (future Expo 2025 growth), Palm Jumeirah (limited inventory = high demand).' },
  { q: 'Can non-residents invest?', a: 'Absolutely. Dubai allows 100% foreign ownership with no residency requirements. We handle all paperwork including residency visa processing if desired.' },
  { q: 'How do I earn returns before completion?', a: 'Many investors flip contracts during construction (permitted in Dubai) for quick profits. We provide market timing advice and buyer matching.' },
  { q: 'What\'s your after-sales support?', a: 'From handover assistance to rental management and resale strategies, we support your investment lifecycle—all under one roof.' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// -----------------------------------------------------------------------------
// Sub-components to prevent re-renders and enable lazy loading
// -----------------------------------------------------------------------------

const DeveloperCard = memo(({ dev }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isMajid = dev.name === 'Majid Al Futtaim';
  const isDeyaar = dev.name === 'Deyaar';
  const isDamac = dev.name === 'Damac';
  const isMeraas = dev.name === 'Meraas';
  const isDanube = dev.name === 'Danube';
  const isDubaiSouth = dev.name === 'Dubai South';

  // Helper for dynamic links
  const getLink = () => {
    if (isDeyaar) return "/deyaar";
    if (isDamac) return "/damac";
    if (isMeraas) return "/meraas";
    if (isDanube) return "/danube";
    if (isMajid) return "/majid-al-futtaim";
    if (dev.name === 'Binghatti') return "/binghatti";
    if (isDubaiSouth) return "/dubai-south";
    return "#";
  };

  return (
    <div
      ref={cardRef}
      className="relative rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center text-center hover:shadow-2xl transition-shadow duration-200 overflow-hidden min-h-[320px] justify-center bg-gray-100"
      style={{
        backgroundImage: isVisible ? `url(${dev.img})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.3s ease-in-out'
      }}
    >
      <div className={isMajid ? "absolute inset-0 bg-black/80 z-0" : "absolute inset-0 bg-black/60 z-0"} />
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full p-8 gap-4">
        <h3 className={isMajid ? "text-2xl font-light text-white font-sans drop-shadow-xl" : "text-2xl font-light text-white font-sans drop-shadow-lg"} style={{ fontFamily: 'Roboto, sans-serif' }}>{dev.name}</h3>
        <p className={isMajid ? "text-white/90 font-normal drop-shadow-xl font-sans" : "text-white/90 font-normal drop-shadow font-sans"} style={{ fontFamily: 'Roboto, sans-serif' }}>{dev.summary}</p>
        <Link to={getLink()} className="px-6 py-2 rounded-lg bg-transparent text-white font-semibold shadow border border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">
          Learn More
        </Link>
      </div>
    </div>
  );
});

const ServiceCard = memo(({ card, idx, cardImage }) => {
  const isExternal = card.path && card.path.startsWith('http');

  const CardContent = (
    <>
      {/* Lazy load background image via standard img tag with absolute positioning for better browser optimization */}
      <img
        src={cardImage}
        alt={card.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/70 transition-all duration-300 pointer-events-none" />

      {/* Content */}
      <div className="absolute left-1/2 bottom-0 z-10 flex flex-col items-center text-center w-[90%] max-w-[90%] p-6 transform -translate-x-1/2 transition-all duration-300 group-hover:-translate-y-8">
        <div className="text-2xl font-sans font-medium text-white mb-2 drop-shadow-lg" style={{ fontFamily: 'Roboto, sans-serif' }}>{card.title}</div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-base px-2 drop-shadow-lg" style={{ fontFamily: 'Roboto, sans-serif' }}>
          {card.desc}
        </div>
      </div>
    </>
  );

  if (isExternal) {
    return (
      <a
        href={card.path}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden min-w-[280px] md:min-w-[340px] max-w-[340px] w-[85vw] md:w-[340px] h-[400px] md:h-[500px] flex items-end justify-center snap-center bg-gray-200"
      >
        {CardContent}
      </a>
    );
  }

  return (
    <Link
      to={card.path}
      className="group relative rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden min-w-[280px] md:min-w-[340px] max-w-[340px] w-[85vw] md:w-[340px] h-[400px] md:h-[500px] flex items-end justify-center snap-center bg-gray-200"
    >
      {CardContent}
    </Link>
  );
});

function Homepage() {
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const servicesContainerRef = useRef(null);
  // Navbar scroll state
  const [navSolid, setNavSolid] = useState(false);

  // Optimized Scroll Listener
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const heroHeight = heroRef.current ? heroRef.current.offsetHeight : 0;
          const aboutHeight = aboutRef.current ? aboutRef.current.offsetHeight : 0;
          const threshold = heroHeight + aboutHeight - 100;

          setNavSolid(window.scrollY > threshold + 40);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState(null);

  // Scroll handlers for Services
  const scrollLeft = () => {
    if (servicesContainerRef.current) {
      servicesContainerRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (servicesContainerRef.current) {
      servicesContainerRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  // Card images for the interactive section
  const cardImages = [
    '/images/pexels-alexazabache-3254725.jpg', // Buy
    '/pic3.jpg',      // Sell
    '/images/pexels-mo-ismail-2130628-3763190.jpg', // Rent
    '/images/pexels-amar-saleem-15661-70441.jpg',   // Property Management
    '/images/pexels-pranjalsrivastava9-2403251.jpg' // Holiday Home
  ];

  const services = [
    { title: 'Buy', desc: 'Explore handpicked villas, townhouses, and apartments across Dubai.', path: '/buy' },
    { title: 'Sell', desc: 'Get expert valuation backed by market insights and exposure.', path: '/sell' },
    { title: 'Rent', desc: 'Flexible rental options for all lifestyles and budgets.', path: '/rent' },
    { title: 'Property Management', desc: 'Professional end-to-end property care for local and overseas owners.', path: '/property-management' },
    { title: 'Holiday Home', desc: 'Short-term luxury rentals with full service and visibility.', path: 'https://ygiholidayhomes.com/' },
  ];

  return (
    <div className="flex flex-col min-h-screen font-sans bg-white">
      <Navbar />

      {/* Hero Section (Video Background) */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center bg-black">
        <video
          className="absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata" // Optimize loading
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black/40 z-10" />
        <div className="relative z-30 flex flex-col items-center text-center w-full px-4" style={{ minHeight: '80vh', display: 'flex', justifyContent: 'flex-end', paddingBottom: '3rem' }}>
          <h1 className="font-sans text-3xl md:text-5xl font-light text-white drop-shadow mb-4 leading-snug tracking-tight" style={{ fontFamily: 'Roboto, sans-serif', textShadow: '0 1px 4px rgba(0,0,0,0.18)' }}>Live Where Dubai's Luxury Meets Lifestyle<br /><span className="block font-sans font-light mt-2" style={{ fontFamily: 'Roboto, sans-serif' }}>only with Alpha Ahead Estate</span></h1>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-6">
            <a href="/contact" className="px-8 py-2.5 rounded-lg bg-white/10 text-white text-lg shadow border border-white/40 hover:bg-white hover:text-primary transition-colors duration-200 tracking-wide" style={{ letterSpacing: '0.5px', textShadow: '0 1px 4px rgba(0,0,0,0.15)' }}>Contact Us</a>
            <a href="#search" className="px-8 py-2.5 rounded-lg bg-white/10 text-white text-lg shadow border border-white/40 hover:bg-white hover:text-primary transition-colors duration-200 tracking-wide" style={{ letterSpacing: '0.5px', textShadow: '0 1px 4px rgba(0,0,0,0.15)' }}>Search Properties</a>
          </div>
        </div>
      </section>

      {/* Developers Section */}
      <section id="developers" className="relative w-full flex items-center justify-center py-24 px-4 bg-white">
        <div className="relative max-w-6xl w-full mx-auto flex flex-col items-center z-10">
          <h2 className="text-4xl md:text-5xl mb-8 text-center font-sans font-light text-gray-900 tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>Trusted Developers in Dubai</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 w-full">
            {developers.map((dev) => (
              <DeveloperCard key={dev.name} dev={dev} />
            ))}
          </div>
        </div>
      </section>

      {/* Map Divider */}
      <div className="w-full flex justify-center my-16">
        <div className="w-full max-w-4xl rounded-2xl shadow-xl overflow-hidden border-2 border-dark-600">
          <img src="/map2.webp" alt="Dubai Map" className="w-full h-[300px] md:h-[400px] object-cover object-center" loading="lazy" width="1200" height="400" />
        </div>
      </div>

      {/* Interactive Services Card Section */}
      <section ref={aboutRef} id="about" className="relative w-full flex items-center justify-center py-24 px-4 bg-white">
        <div className="relative max-w-6xl w-full mx-auto flex flex-col items-center z-10">
          <h2 className="text-4xl md:text-5xl mb-8 text-center font-sans font-light text-gray-900 tracking-tight" style={{ fontFamily: 'Roboto, sans-serif' }}>Our Services</h2>
          <p className="text-lg text-gray-700 text-center mb-12 max-w-4xl">From buying and renting premium properties to investment advisory and property management, we offer end-to-end real estate solutions in Dubai.</p>
          <div className="relative w-full services-wrapper">
            {/* Left Button */}
            <button
              onClick={scrollLeft}
              className="services-nav-btn absolute left-4 top-1/2 -translate-y-1/2 z-20 p-4 rounded-full bg-white/80 backdrop-blur-md shadow-lg hover:shadow-xl border border-white/50 text-gray-800 transition-all duration-300 hover:scale-110 focus:outline-none hidden md:flex items-center justify-center opacity-0"
              aria-label="Scroll left"
            >
              <FaChevronLeft className="w-5 h-5" />
            </button>

            <div
              ref={servicesContainerRef}
              className="flex flex-nowrap overflow-x-auto gap-6 w-full pb-8 pt-4 px-4 snap-x snap-mandatory scrollbar-hide scroll-smooth"
              style={{ scrollPaddingLeft: '24px', scrollPaddingRight: '24px' }}
            >
              {services.map((card, idx) => (
                <ServiceCard key={card.title} card={card} idx={idx} cardImage={cardImages[idx]} />
              ))}
            </div>

            {/* Right Button */}
            <button
              onClick={scrollRight}
              className="services-nav-btn absolute right-4 top-1/2 -translate-y-1/2 z-20 p-4 rounded-full bg-white/80 backdrop-blur-md shadow-lg hover:shadow-xl border border-white/50 text-gray-800 transition-all duration-300 hover:scale-110 focus:outline-none hidden md:flex items-center justify-center opacity-0"
              aria-label="Scroll right"
            >
              <FaChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Call-to-Action Banner (Investment Highlight) */}
      <section className="relative w-full py-20 px-4 bg-gray-900 overflow-hidden">
        <img
          src="/property2.jpg"
          alt="Investment Highlight"
          className="absolute inset-0 w-full h-full object-cover z-0"
          loading="lazy"
          width="1200"
          height="600"
        />
        <div className="absolute inset-0 bg-black/60 z-0" />
        <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
          <h2 className="font-sans text-3xl md:text-4xl font-light mb-4 drop-shadow-lg" style={{ fontFamily: 'Roboto, sans-serif' }}>Discover Dubai’s Finest Off-Plan Property Investments</h2>
          <p className="mb-6 text-lg font-medium drop-shadow" style={{ fontFamily: 'Roboto, sans-serif' }}>From waterfront villas in The Oasis to skyline apartments in Downtown Dubai —<br />Alpha Ahead connects you to exclusive, tax-free investment opportunities in Dubai’s most desirable communities.</p>
          <a href="#" className="inline-block px-8 py-4 rounded-lg bg-transparent text-white font-semibold text-lg shadow-lg border border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">Browse Investments</a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-3xl mx-auto py-20 px-4 bg-white">
        <h2 className="font-sans text-3xl md:text-4xl font-light text-gray-900 mb-10 text-center" style={{ fontFamily: 'Roboto, sans-serif' }}>Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div key={faq.q} className="border border-dark-600 rounded-xl bg-card shadow-sm">
              <button
                className="w-full flex justify-between items-center px-6 py-4 text-lg font-semibold text-white text-left focus:outline-none"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                aria-expanded={openFaq === idx}
              >
                <span>{faq.q}</span>
                <svg className={classNames('w-6 h-6 transition-transform', openFaq === idx ? 'rotate-180' : '')} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {openFaq === idx && (
                <div className="px-6 pb-4 text-gray-300 text-base animate-fadeIn">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <a href="#contact" className="inline-block px-6 py-3 rounded-lg bg-transparent text-white font-semibold shadow border border-white/40 hover:bg-white hover:text-primary transition-colors duration-200">Have more questions? Contact Us</a>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Homepage;
/* Hide scrollbar utility */
<style>{`
  .scrollbar-hide::-webkit-scrollbar { display: none; }
  .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
  /* Hover effect for services navigation buttons */
  .services-wrapper:hover .services-nav-btn { opacity: 1; }
`}</style>